import React from "react";
import { Flex } from "styles/utility-components";

import MultiSelectDropdown from "lib/core_components/MultiSelectDropdownList";
import { assertCurrentUser } from "lib/currentUser";
import { Team, useTeams } from "lib/hooks/api/teams/useTeams";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { sysPrimaryDefault } from "lib/themes/colors";

type Props = {
  onChange: (teamIds: string[]) => void;
  teamIds: string[];
};

export function TeamDropdownFilter({ teamIds, onChange }: Props) {
  const currentUser = assertCurrentUser();
  const currentTeamId = currentUser.team.id;

  const { data: teams, isLoading: isLoadingTeam } = useTeams();

  const currentTeam = teams?.find((team) => team.id === currentTeamId);
  const otherTeams = teams?.filter((team) => team.id !== currentTeamId) ?? [];

  // Display the user's current team at the top of the list
  const companyTeams = currentTeam ? [currentTeam, ...otherTeams] : [...otherTeams];
  const teamOptions = companyTeams.map((team) => ({ title: team.name, value: team.id })) ?? [];

  return (
    <MultiSelectDropdown
      title={
        <Flex gap={8} alignItems="center">
          <UIcon icon="users01" color={sysPrimaryDefault} />
          {getSelectDropdownLabel(teamIds, companyTeams, currentTeamId)}
        </Flex>
      }
      fieldName="team"
      searchable
      hideSelectAll
      skipSort
      options={teamOptions}
      selectedOptions={teamIds}
      onChange={onChange}
      loading={isLoadingTeam}
      showSelectedSizeBadge={false}
    />
  );
}

function getSelectDropdownLabel(
  selectedTeamIds: string[],
  allTeams: Team[],
  currentTeamId: string,
) {
  const numSelectedTeams = selectedTeamIds.length;
  const totalTeams = allTeams.length;

  const getTeamNameById = (id: string) => {
    const team = allTeams.find((team) => team.id === id);
    return team ? team.name : null;
  };

  if (numSelectedTeams === 0) return "All teams";

  if (numSelectedTeams === 1) {
    return selectedTeamIds[0] === currentTeamId ? "My team" : getTeamNameById(selectedTeamIds[0]);
  }

  if (numSelectedTeams < totalTeams) {
    return `${numSelectedTeams} teams`;
  }

  return "All teams";
}
